import { IonToast } from '@ionic/react';
import {
	Comment,
	DocumentTypes,
	EmptyNewComment,
	FullAuthored,
	FullComment,
	Post,
	PostTypes,
	getLoadingMessage,
	getReplyTitle
} from 'model';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useCreateCommentMutation, useGetCommentQuery } from '../api/CommentApi';
import { useGetPostQuery } from '../api/PostApi';
import { AuthorHeader } from '../components/AuthorHeader';
import { TextArea } from '../components/Forms/TextArea';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { useLoading } from '../hooks/useQuirkLoading';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';
import styles from './ReplyComment.module.css';
import { PostHeader } from '../components/PostHeader';
import { SkeletonLoader } from '../components/SkeletonLoader.js';
import { PrivateConversationBanner } from '../components/PrivateConversationBanner.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';

const CommentItem: React.FC<{ comment: FullComment | FullAuthored<Comment> }> = ({ comment }) => (
	<div className={styles.commentContainer}>
		<div className={postStyles.postName}>
			<AuthorHeader authoredItem={comment} />
		</div>
		<div className={styles.comment}>{comment?.comment}</div>
	</div>
);

export const ReplyComment: React.FC = () => {
	const user = useCurrentUser();
	const { commentid, postid } = useParams<{ commentid: Comment['_id']; postid: Post['_id'] }>();
	const { data, isLoading } = useGetCommentQuery(commentid);
	const { data: post, isLoading: postIsLoading } = useGetPostQuery(postid, { skip: !postid });
	useLoading({ isLoading: isLoading || postIsLoading });

	return (
		<ReplyItem parent={commentid} parentType="comment" root={data?.root} rootType="post">
			<>
				{data && (
					<>
						<CommentItem comment={data} />
						{post && post.postType === 'marketplaceItem' && (
							<PrivateConversationBanner seller={user._id === post?.author} />
						)}
					</>
				)}
			</>
		</ReplyItem>
	);
};

export const ReplyPost: React.FC = () => {
	const { postid } = useParams<{ postid: Post['_id'] }>();
	const { data } = useGetPostQuery(postid, { skip: !postid });
	const user = useCurrentUser();
	return (
		<ReplyItem parent={postid} parentType="post" root={postid} rootType="post" postType={data?.postType}>
			<SkeletonLoader height="75px">
				{data !== undefined ? <PostHeader post={data} compact /> : null}
				{data !== undefined && data.postType === 'marketplaceItem' ? (
					<PrivateConversationBanner seller={user._id === data?.author} />
				) : null}
			</SkeletonLoader>
		</ReplyItem>
	);
};

const ReplyItem: React.FC<{
	parent: string;
	root?: string;
	parentType: DocumentTypes;
	rootType: DocumentTypes;
	postType?: PostTypes;
	children: JSX.Element;
}> = ({ parent, root, parentType, rootType, postType, children }) => {
	const history = useHistory();
	const { source, postid } = useParams<{ postid: Post['_id']; source: string }>();
	const [comment, setComment] = useState<Comment>({
		...EmptyNewComment,
		root: root || '',
		rootType,
		parent,
		parentType
	});
	const [createComment, { isLoading: updateIsLoading, isError: updateIsError, error: updateError }] =
		useCreateCommentMutation();
	useLoading({
		isLoading: updateIsLoading,
		message: getLoadingMessage(postType)
	});
	const onSubmit = async () => {
		const result = await createComment({
			comment: { ...comment, root: root || '', rootType } as Comment
		});
		if (!('error' in result)) {
			// Clear the comment
			setComment({
				...EmptyNewComment,
				root: root || '',
				rootType,
				parent,
				parentType
			});
			if (!source || source === 'detail') {
				history.go(-1);
			} else {
				history.replace(`/main/latest/post/show/${postid}`);
			}
		}
	};
	return (
		<QuirkIonPage name="Reply" data={{ parent, parentType, rootType }}>
			<Header
				left={<IconButton icon={<IconClose />} onClick={() => history.go(-1)} />}
				right={
					<IconButton
						label="Post"
						icon={<IconCheck />}
						disabled={comment?.comment?.length === 0}
						onClick={() => {
							onSubmit();
						}}
					/>
				}
			/>

			<QuirkIonContent>
				<>
					<div className={styles.replyContainer}>{children}</div>
					<>
						<div className={styles.composeTextAreaContainer}>
							<TextArea
								placeholder={`Type your ${getReplyTitle(postType as PostTypes)}...`}
								value={comment.comment}
								name="reply"
								rows={9}
								onChange={e => setComment({ ...comment, comment: e.currentTarget.value || '' })}
								disabled={false}
								testId={`EditPostContent-${postid}`}
							/>
							<div className={styles.postButtonContainer}>
								<IconButton
									disabled={comment?.comment?.length === 0}
									onClick={() => {
										onSubmit();
									}}
									label="Post"
									icon={<IconCheck />}
									testId={`EditCommentPostButton-${postid}`}
								/>
							</div>
						</div>
					</>
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message={(updateError as any)?.data}
						position="top"
						color="danger"
					/>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
